<script setup lang="ts">
import { onMounted, ref } from 'vue'
import {
  NCard, NButton, NSpace, NAlert, NDescriptions, NDescriptionsItem,
  NForm, NFormItem, NInput, NTag, NText,
} from 'naive-ui'
import { githubAPI, type GitHubConfig, type GitHubConfigInput } from '../../api'
import { message } from '../../message'

const githubLoading = ref(false)
const githubSaving = ref(false)
const githubTesting = ref(false)
const githubLoadErr = ref('')
const githubForm = ref<GitHubConfigInput>({
  default_repo: '',
  pat: '',
})
const githubState = ref<GitHubConfig | null>(null)
const patInputProps = {
  autocomplete: 'new-password',
  name: 'sm-github-pat',
}

function testStatusText(config: GitHubConfig | null): string {
  if (!config || config.last_test_ok == null) return '未验证'
  return config.last_test_ok ? '通过' : '失败'
}

function testStatusType(config: GitHubConfig | null): 'default' | 'success' | 'error' {
  if (!config || config.last_test_ok == null) return 'default'
  return config.last_test_ok ? 'success' : 'error'
}

function errMsg(e: unknown): string {
  if (e instanceof Error) return e.message || 'unknown error'
  if (typeof e === 'string') return e
  try { return JSON.stringify(e) } catch { return String(e) }
}

function applyGitHubConfig(config: GitHubConfig) {
  githubState.value = config
  githubForm.value = {
    default_repo: config.default_repo ?? '',
    pat: '',
  }
}

async function loadGitHubConfig() {
  githubLoading.value = true
  githubLoadErr.value = ''
  try {
    applyGitHubConfig(await githubAPI.getConfig())
  } catch (e) {
    githubLoadErr.value = errMsg(e)
    message.error(`无法获取 GitHub 集成配置：${githubLoadErr.value}`)
  } finally {
    githubLoading.value = false
  }
}

async function saveGitHubConfig() {
  if (!githubState.value?.has_pat && !githubForm.value.pat?.trim()) {
    message.error('PAT 尚未保存。请手动粘贴 PAT 后再保存。')
    return
  }
  githubSaving.value = true
  try {
    const saved = await githubAPI.saveConfig(githubForm.value)
    applyGitHubConfig(saved)
    message.success('GitHub 配置已保存')
  } catch (e) {
    message.error(`保存 GitHub 配置失败：${errMsg(e)}`)
  } finally {
    githubSaving.value = false
  }
}

async function testGitHubConfig() {
  githubTesting.value = true
  try {
    const result = await githubAPI.testConfig({ pat: githubForm.value.pat })
    if (result.config) applyGitHubConfig(result.config)
    else {
      githubState.value = {
        ...(githubState.value ?? {
          default_owner: '',
          default_repo: githubForm.value.default_repo,
          has_pat: false,
        }),
        default_repo: githubForm.value.default_repo,
      }
    }
    message.success('GitHub 连接测试成功')
  } catch (e) {
    message.error(`GitHub 连接测试失败：${errMsg(e)}`)
  } finally {
    githubTesting.value = false
  }
}

onMounted(loadGitHubConfig)
</script>

<template>
  <NCard title="GitHub & GHCR 认证">
    <NAlert type="info" show-icon style="margin-bottom:12px">
      <template #header>只需要配置 GitHub PAT</template>
      用于写入仓库 Secret、提交 workflow，以及生成 GHCR 私有镜像拉取认证。
    </NAlert>
    <NAlert v-if="githubLoadErr" type="error" :show-icon="true" style="margin-bottom:12px">
      读取 GitHub 配置失败：{{ githubLoadErr }}
      <NButton size="small" style="margin-left:12px" @click="loadGitHubConfig">重试</NButton>
    </NAlert>
    <NDescriptions v-if="githubState" :column="1" label-placement="left" bordered size="small" style="margin-bottom:12px">
      <NDescriptionsItem label="PAT 状态">
        <NTag :type="githubState.has_pat ? 'success' : 'warning'">{{ githubState.has_pat ? '已配置' : '未配置' }}</NTag>
      </NDescriptionsItem>
      <NDescriptionsItem label="GitHub Owner">
        <NSpace align="center" size="small">
          <NText>{{ githubState.default_owner || '未识别' }}</NText>
          <NTag size="small" :bordered="false">由 PAT 自动识别</NTag>
        </NSpace>
      </NDescriptionsItem>
      <NDescriptionsItem label="最近验证">
        <NTag :type="testStatusType(githubState)">{{ testStatusText(githubState) }}</NTag>
      </NDescriptionsItem>
      <NDescriptionsItem v-if="githubState.last_test_error" label="失败原因">{{ githubState.last_test_error }}</NDescriptionsItem>
    </NDescriptions>
    <NForm label-placement="top" :model="githubForm" :disabled="githubLoading || githubSaving || githubTesting">
      <NFormItem label="GitHub PAT">
        <NInput
          v-model:value="githubForm.pat"
          type="password"
          show-password-on="click"
          :placeholder="githubState?.has_pat ? '留空表示保留现有 PAT' : '粘贴 fine-grained 或 classic PAT'"
          :input-props="patInputProps"
        />
      </NFormItem>
      <NFormItem label="默认仓库名（可选）">
        <NInput v-model:value="githubForm.default_repo" placeholder="例如：widget" />
        <template #feedback>只用于接入向导默认填充；具体服务仍可选择自己的仓库。</template>
      </NFormItem>
    </NForm>
    <NSpace>
      <NButton type="primary" :loading="githubSaving" :disabled="githubLoading || githubTesting" @click="saveGitHubConfig">保存 GitHub 配置</NButton>
      <NButton :loading="githubTesting" :disabled="githubLoading || githubSaving" @click="testGitHubConfig">测试连接</NButton>
    </NSpace>
  </NCard>
</template>
